import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Client, IMessage } from '@stomp/stompjs';
import SockJS from 'sockjs-client';

import {
  bridgeAtom,
  cartAtom,
  chatIdAtom,
  errorAtom,
  isOpenChatAtom,
  isResetChatAtom,
  isSessionSentFirstTimeAtom,
  messagesAtom,
  messagesReceivedTimeAtom,
  renderedMessagesAtom,
  tokenAtom,
  useAtom,
  useSetValue,
  useValue,
  widgetPopupsStatesAtom,
} from '@/store';

import { AppEvents, getProductIdType } from '@/utils';

import { MESSAGE_LIMIT, RefreshErrorTypes, setCookie, useEvent } from '@/modules/common';
import {
  ErrorType,
  MessageReasonType,
  MessageSenderType,
  MessageType,
  ModelActionType,
  PageType,
  PopupState,
  WidgetPopupType,
} from '@/modules/models';
import { TOKEN_STORAGE } from '@/store/constants';

import { openChat } from '../api.service';
import { RECONNECT_TIMEOUT, SESSION_LIFETIME } from './constants';
import { useSessionFinishTime } from './sessionFinishTime.hook';
import { getSocketUrl } from './utils';

const MESSAGES_API = '/app/sendMessage';
const DEFAULT_CROSS_SALE_DELAY = 1500;

export type ReturnType = {
  initConversation: () => void;
  sendMessage: (message: MessageType) => Promise<{ error: ErrorType } | void>;
  isSubscribed: boolean;
};

export type UseStompClientType = {
  onReset: () => void;
  onMessageReceived: (message: MessageType) => void;
  onHistoryReceived: (messages: MessageType[]) => void;
};

export const useStompClient = (props: UseStompClientType): ReturnType => {
  const propsRef = useRef(props);
  const isSendOpenChat = useRef(false);

  const setToken = useSetValue(tokenAtom);
  const setMessagesReceivedTime = useSetValue(messagesReceivedTimeAtom);
  const [chatId, setChatId] = useAtom(chatIdAtom);
  const [isResetChat, setIsResetChat] = useAtom(isResetChatAtom);

  const [error, setError] = useAtom(errorAtom);
  const prevErrorRef = useRef(error);

  const isOpenChat = useValue(isOpenChatAtom);
  const renderedMessages = useValue(renderedMessagesAtom);
  const messages = useValue(messagesAtom);
  const isSessionSentFirstTime = useValue(isSessionSentFirstTimeAtom);
  const { isOpen: isCartOpen, products } = useValue(cartAtom);
  const [widgetPopupsState, setWidgetPopupsState] = useAtom(widgetPopupsStatesAtom);
  const { CROSS_SALE } = widgetPopupsState;

  const {
    productId,
    variantId,
    pageType,
    isAlwaysOpenWSConnection,
    isSupportSubDomainWithCookie,
    crossSaleMessage,
    pageType: bridgePageType,
  } = useValue(bridgeAtom);

  const [isActiveTab, setIsActiveTab] = useState(true);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [stompClient, setStompClient] = useState<Client | null>(null);

  const benchmarkedMessages = useRef<Record<number, boolean>>({});

  const onReset = useCallback(() => {
    propsRef.current.onReset();
    isSendOpenChat.current = false;

    setStompClient((prev) => {
      prev?.deactivate();
      return null;
    });

    setIsSubscribed(false);
  }, []);

  const setSessionFinishTime = useSessionFinishTime(onReset);

  const initConversation = useCallback(() => {
    if (!stompClient?.connected || (!isResetChat && !chatId)) {
      setError(ErrorType.EmptyChatId);
      return;
    }

    try {
      const { product_id: id, pageType: type } = getProductIdType({
        product_id: productId,
        pageType: isCartOpen ? PageType.CART : pageType,
      });

      stompClient.publish({
        destination: MESSAGES_API,
        body: JSON.stringify({
          content: '',
          product_id: id,
          variant_id: variantId,
          page_type: bridgePageType || type,
          role: MessageSenderType.USER,
          reason: MessageReasonType.INITIALIZATION,
          clientTimestamp: Date.now(),
          cart: products.map(({ handle }) => handle),
        }),
      });
      setWidgetPopupsState((prevState) => ({
        ...prevState,
        [WidgetPopupType.CROSS_SALE]: PopupState.SEEN,
      }));
      setIsResetChat(false);
    } catch (_e) {
      setError(ErrorType.NotInitialized);
    }
  }, [
    bridgePageType,
    stompClient,
    chatId,
    setError,
    productId,
    isCartOpen,
    pageType,
    variantId,
    products,
    isResetChat,
    setIsResetChat,
    setWidgetPopupsState,
  ]);

  const getSuggestion = useCallback(
    (e: CustomEvent<{ productsList: string[] }>) => {
      const { productsList } = e.detail;
      if (!stompClient?.connected) {
        return;
      }

      try {
        stompClient.publish({
          destination: MESSAGES_API,
          body: JSON.stringify({
            content: '',
            page_type: 'ADD_TO_CART_SUGGESTION',
            role: MessageSenderType.USER,
            reason: MessageReasonType.INITIALIZATION,
            clientTimestamp: Date.now(),
            cart: productsList,
          }),
        });
      } catch (e) {
        console.log(e);
      }
    },
    [stompClient]
  );

  const sendBenchmark = useCallback(
    (messageId: string) => {
      const message = messages.find(({ id }) => String(id) === messageId);

      if (!message) {
        return;
      }

      const { id, role, replyMessageId } = message;

      if (!id) {
        return;
      }

      const isMessageBenchmarked = Boolean(renderedMessages?.[id] && benchmarkedMessages.current?.[id]);

      if (role !== MessageSenderType.ASSISTANT || !replyMessageId || !stompClient?.connected || isMessageBenchmarked) {
        return;
      }

      stompClient.publish({
        destination: '/app/messageAck',
        body: JSON.stringify({
          messageId: replyMessageId,
          receivedAt: new Date().getTime(),
        }),
      });

      benchmarkedMessages.current = {
        ...benchmarkedMessages.current,
        [id]: true,
      };
    },
    [stompClient, messages, renderedMessages]
  );

  const getCrossSaleMessage = useCallback(() => {
    if (!isOpenChat || !stompClient?.connected) {
      return;
    }

    try {
      setWidgetPopupsState((prevState) => ({
        ...prevState,
        [WidgetPopupType.CROSS_SALE]: PopupState.SEEN,
      }));
      stompClient.publish({
        destination: MESSAGES_API,
        body: JSON.stringify({
          content: '',
          page_type: pageType,
          role: MessageSenderType.USER,
          reason: MessageReasonType.CROSS_SALE,
          clientTimestamp: Date.now(),
        }),
      });
    } catch (e) {
      console.log(e);
    }
  }, [isOpenChat, stompClient, pageType, setWidgetPopupsState]);

  const onHistory = useCallback(
    (resp: IMessage) => {
      const messages = JSON.parse(resp.body) as MessageType[];

      // Filter out messages with the same content
      const uniqueMessagesMap = messages.reduce((map, message) => {
        if (message.id) {
          map.set(message.content, message);
        }
        return map;
      }, new Map<string, MessageType>());

      const filteredMessages = Array.from(uniqueMessagesMap.values());

      if (filteredMessages.length === 0) {
        // initial finish time set
        setSessionFinishTime();
        return;
      }

      const latestTimestamp = filteredMessages.reduce((time, m) => {
        if (m?.action === ModelActionType.SAY && m.clientTimestamp && m.clientTimestamp > time) {
          return m.clientTimestamp;
        }

        return time;
      }, 0);

      // session finish time set based on latest message
      const isLiveSession = setSessionFinishTime(latestTimestamp + SESSION_LIFETIME);
      const urlParams = new URLSearchParams(window.location.search);
      const chatIdParam = urlParams.get('chatId');

      // restore history only if it's a live session
      if (isLiveSession) {
        propsRef.current.onHistoryReceived(filteredMessages);
        filteredMessages.forEach(
          ({ id }) =>
            id &&
            setMessagesReceivedTime((prev) => ({
              ...prev,
              [id]: new Date().getTime(),
            }))
        );
        setIsSubscribed(true);
      } else {
        // delete chatId from query params to avoid infinite chat reset
        if (chatIdParam) {
          const url = new URL(window.location.href);
          url.searchParams.delete('chatId');
          window.history.replaceState({}, '', url.toString());
        }
        onReset();
      }

      messages.forEach(({ id }) => {
        if (!id) {
          return;
        }
        benchmarkedMessages.current = {
          ...benchmarkedMessages.current,
          [id]: true,
        };
      });
    },

    [setMessagesReceivedTime]
  );

  const onTopic = useCallback(
    (resp: IMessage) => {
      const message = JSON.parse(resp.body) as MessageType;
      propsRef.current.onMessageReceived(message);
      if (message.id && message.role) {
        setMessagesReceivedTime((prev) => ({
          ...prev,
          [String(message.id)]: new Date().getTime(),
        }));
      }
    },
    [setMessagesReceivedTime]
  );

  const connectToWebSocket = useCallback(async () => {
    setIsSubscribed(false);

    const url = getSocketUrl();

    let client: Client;
    const socket = new SockJS(url);

    const onConnect = (principal: string) => {
      client.subscribe('/app/register', (response) => {
        const data = JSON.parse(response.body);
        const token = data.content?.content;

        setChatId(data.chatId);
        setToken(token);

        if (isSupportSubDomainWithCookie && token) {
          setCookie({
            days: 7,
            value: token,
            name: TOKEN_STORAGE,
            useRootDomain: token,
          });
        }

        client.subscribe('/app/history', onHistory);
        client.subscribe('/topic/' + principal, onTopic);

        setIsSubscribed(true);
      });
    };

    const onClose = () => {
      client?.deactivate();
      setStompClient(null);
    };

    const onError = () => {
      // Reconnect only if the tab is active
      if (document.visibilityState === 'visible') {
        setTimeout(connectToWebSocket, RECONNECT_TIMEOUT);
      }
    };

    client = await new Promise((resolve) => {
      client = new Client({
        onStompError: onError,
        onWebSocketError: onError,
        onDisconnect: onClose,
        onWebSocketClose: onClose,
        webSocketFactory: () => socket,
        onConnect: (frame) => {
          const principal = frame.headers['user-name'];
          onConnect(principal);
          resolve(client);
        },
      });

      client.activate();
    });

    setStompClient(client);

    return client;
  }, []);

  const sendMessage = useCallback(
    async (message: MessageType) => {
      setSessionFinishTime();

      if (message.content?.length > MESSAGE_LIMIT) {
        setError(ErrorType.MessageTooLong);
        return {
          error: ErrorType.MessageTooLong,
        };
      }

      let client = stompClient;

      if (!stompClient?.connected) {
        client = await connectToWebSocket();
      }

      if (!client?.connected) {
        setError(ErrorType.ConnectionFailure);
        return {
          error: ErrorType.ConnectionFailure,
        };
      }

      try {
        client.publish({
          destination: MESSAGES_API,
          body: JSON.stringify(message),
        });
      } catch (_e) {
        setError(ErrorType.MessageNotDelivered);
        return {
          error: ErrorType.MessageNotDelivered,
        };
      }
    },
    [connectToWebSocket, setError, setSessionFinishTime, stompClient]
  );

  useEffect(() => {
    propsRef.current = props;
  }, [props]);

  useLayoutEffect(() => {
    const onVisibilityChange = () => {
      setIsActiveTab(document.visibilityState === 'visible');
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, []);

  useEffect(() => {
    const onOpenChat = async () => {
      try {
        await openChat(productId);
        isSendOpenChat.current = true;
      } catch (e) {
        console.error('Error in sendOpenedChatRequest:', e);
      }
    };

    if (!isSendOpenChat.current && isOpenChat && isSubscribed && isSessionSentFirstTime) {
      onOpenChat();
    }
  }, [isSessionSentFirstTime, isOpenChat, isSubscribed, productId]);

  useEffect(() => {
    if ((isAlwaysOpenWSConnection || isOpenChat) && isActiveTab && !stompClient) {
      connectToWebSocket();
    }
  }, [isOpenChat, stompClient, isActiveTab, isAlwaysOpenWSConnection]);

  useEffect(() => {
    if (prevErrorRef.current && !error) {
      if (RefreshErrorTypes.includes(prevErrorRef.current)) {
        setStompClient((prev) => {
          prev?.deactivate();
          return null;
        });
      }
    }

    prevErrorRef.current = error;
  }, [error]);

  useEffect(() => {
    Object.keys(renderedMessages).forEach((key) => sendBenchmark(key));
  }, [renderedMessages, sendBenchmark]);

  useEvent({ name: AppEvents.GET_SUGGESTION, handler: getSuggestion });

  useEffect(() => {
    if (!isOpenChat || CROSS_SALE !== PopupState.STARTED) {
      return;
    }

    const timer = setTimeout(
      () => {
        getCrossSaleMessage();
      },
      typeof crossSaleMessage === 'number' ? crossSaleMessage : DEFAULT_CROSS_SALE_DELAY
    );

    return () => clearTimeout(timer);
  }, [isOpenChat, CROSS_SALE, crossSaleMessage, getCrossSaleMessage]);

  return { sendMessage, initConversation, isSubscribed };
};
